// Guide §11.1 — the ONE sale-maths function. App and server both run it; order of steps must not change.
import { allocate, D, Decimal, money, round2, roundToStep, sum, ZERO } from '../money';
import type { MoneyInput, RoundingMode, RoundingStep } from '../money';

export type DiscountSpec = { type: 'percent' | 'amount'; value: MoneyInput };

export interface DraftLine {
  /** Quantity in the unit sold (3 dp) */
  qty: MoneyInput;
  /** Price per sold unit */
  unitPrice: MoneyInput;
  /** Base-unit factor of the sold unit (dozen = 12). Default 1 */
  unitFactor?: MoneyInput;
  /** Product average cost per BASE unit at sale time; null/undefined = no cost price */
  costPrice?: MoneyInput | null;
  /** Hand-entered discount for this line */
  discount?: DiscountSpec | null;
}

export interface DraftPayment {
  method: string;
  amount: MoneyInput;
}

export interface SaleDraft {
  lines: DraftLine[];
  /** Bill-level discount (hand-entered or promotion) */
  discount?: DiscountSpec | null;
  payments: DraftPayment[];
  hasCustomer: boolean;
}

export interface LineTotals {
  lineGross: Decimal;
  lineDiscount: Decimal;
  /** Bill-level discount share INCLUDING the rounding share (negative rounding = extra charge). Stored in SaleItem.allocSaleDiscount so Σ(gross − discounts) = net. */
  allocSaleDiscount: Decimal;
  lineNet: Decimal;
  unitCost: Decimal | null;
  lineCost: Decimal | null;
  lineProfit: Decimal | null;
  isLoss: boolean;
}

export type SaleIssue = 'no_lines' | 'negative_total' | 'due_needs_customer' | 'discount_exceeds' | 'line_invalid' | 'overpay_not_change';

export interface SaleTotals {
  lines: LineTotals[];
  subtotal: Decimal;
  lineDiscountTotal: Decimal;
  /** Bill discount amount before rounding */
  saleDiscountAmount: Decimal;
  /** total − (subtotal − lineDiscountTotal − saleDiscountAmount): +ve when rounded up */
  roundingAdj: Decimal;
  total: Decimal;
  paidTotal: Decimal;
  /** Payments beyond total — change handed back, not recorded as revenue */
  change: Decimal;
  dueAmount: Decimal;
  costTotal: Decimal | null;
  grossProfit: Decimal | null;
  isLoss: boolean;
  needsCustomer: boolean;
  issues: SaleIssue[];
}

const discountAmount = (base: Decimal, d: DiscountSpec | null | undefined): Decimal => {
  if (!d) return ZERO;
  const v = D(d.value);
  if (v.lte(0)) return ZERO;
  const amt = d.type === 'percent' ? round2(base.times(v).dividedBy(100)) : round2(v);
  return Decimal.min(amt, base);
};

export interface RoundingOpt {
  rounding: boolean;
  /** ৳1 (default — the only step before v10), ৳5 or ৳10 */
  roundingStep?: RoundingStep;
  /** 'nearest' (default) or 'down' */
  roundingMode?: RoundingMode;
}

export function computeSale(d: SaleDraft, opt: RoundingOpt): SaleTotals {
  const issues: SaleIssue[] = [];
  if (d.lines.length === 0) issues.push('no_lines');

  // 1. lines
  const base = d.lines.map((l) => {
    const qtyD = D(l.qty);
    if (qtyD.lte(0) || D(l.unitPrice).lt(0)) issues.push('line_invalid');
    const gross = round2(qtyD.times(l.unitPrice));
    const disc = discountAmount(gross, l.discount);
    return { gross, disc, net0: gross.minus(disc) };
  });
  const subtotal = sum(base.map((b) => b.gross));
  const lineDiscountTotal = sum(base.map((b) => b.disc));
  const afterLine = subtotal.minus(lineDiscountTotal);

  // 2. bill discount, split over lines by (gross − lineDiscount)
  const saleDiscountAmount = discountAmount(afterLine, d.discount);
  if (d.discount && D(d.discount.value).gt(0) && d.discount.type === 'amount' && D(d.discount.value).gt(afterLine)) {
    issues.push('discount_exceeds');
  }
  const allocSale = allocate(saleDiscountAmount, base.map((b) => b.net0));

  // 3. rounding (nearest ৳1 by default; ৳5 / ৳10, nearest or down, since v10), split the same way
  const preRound = afterLine.minus(saleDiscountAmount);
  const total = opt.rounding ? money(roundToStep(preRound, opt.roundingStep ?? 1, opt.roundingMode ?? 'nearest')) : money(preRound);
  const roundingAdj = total.minus(preRound);
  const allocRound = roundingAdj.isZero() ? base.map(() => ZERO) : allocate(roundingAdj, base.map((b) => b.net0.minus(0)));
  if (total.isNegative()) issues.push('negative_total');

  // 4–6. per line net / cost / profit
  const lines: LineTotals[] = d.lines.map((l, i) => {
    const b = base[i]!;
    const alloc = allocSale[i]!.minus(allocRound[i]!);
    const lineNet = b.gross.minus(b.disc).minus(alloc);
    const hasCost = l.costPrice !== null && l.costPrice !== undefined && l.costPrice !== '';
    const unitCost = hasCost ? D(l.costPrice as MoneyInput) : null;
    const lineCost = unitCost ? round2(D(l.qty).times(l.unitFactor ?? 1).times(unitCost)) : null;
    const lineProfit = lineCost ? lineNet.minus(lineCost) : null;
    return {
      lineGross: b.gross,
      lineDiscount: b.disc,
      allocSaleDiscount: alloc,
      lineNet,
      unitCost,
      lineCost,
      lineProfit,
      isLoss: lineCost !== null && lineNet.lt(lineCost),
    };
  });

  const allCost = lines.length > 0 && lines.every((x) => x.lineCost !== null);
  const costTotal = lines.some((x) => x.lineCost !== null) ? sum(lines.map((x) => x.lineCost ?? ZERO)) : null;
  const grossProfit = allCost && costTotal ? total.minus(costTotal) : null;

  // 7. payments
  const paidRaw = sum(d.payments.map((p) => round2(p.amount)));
  const paidTotal = Decimal.min(paidRaw, total);
  const change = Decimal.max(paidRaw.minus(total), ZERO);
  const dueAmount = total.minus(paidTotal);
  const needsCustomer = dueAmount.gt(0);
  if (needsCustomer && !d.hasCustomer) issues.push('due_needs_customer');

  return {
    lines,
    subtotal,
    lineDiscountTotal,
    saleDiscountAmount,
    roundingAdj,
    total,
    paidTotal,
    change,
    dueAmount,
    costTotal,
    grossProfit,
    isLoss: lines.some((x) => x.isLoss) || (grossProfit !== null && grossProfit.lt(0)),
    needsCustomer,
    issues,
  };
}

/** Server re-check (Guide §11.1): amounts in a payload vs recomputed. ৳0.01 tolerance; mismatch flags calcMismatch but keeps the customer's numbers. */
export function calcMismatch(claimed: { total: MoneyInput; dueAmount: MoneyInput }, computed: SaleTotals): boolean {
  const tol = new Decimal('0.01');
  return D(claimed.total).minus(computed.total).abs().gt(tol) || D(claimed.dueAmount).minus(computed.dueAmount).abs().gt(tol);
}

// ───────────────────────── Returns (Guide §11.4) ─────────────────────────

export interface ReturnableLine {
  saleItemId: string;
  qtySold: MoneyInput;
  returnedQty: MoneyInput;
  lineNet: MoneyInput;
  /** Cost already booked on the sale line (null = no cost) */
  lineCost: MoneyInput | null;
  /** net already reversed by earlier returns of this line */
  netReversedSoFar?: MoneyInput;
  costReversedSoFar?: MoneyInput;
}

export interface ReturnRequestLine {
  saleItemId: string;
  qty: MoneyInput;
  restock: boolean;
}

export interface ReturnLineResult {
  saleItemId: string;
  qty: Decimal;
  restock: boolean;
  netReversed: Decimal;
  costReversed: Decimal | null;
  final: boolean;
}

export interface ReturnTotals {
  lines: ReturnLineResult[];
  totalReversed: Decimal;
  /** Part that reduces the customer's baki on this invoice (return_credit) */
  bakiAdjust: Decimal;
  /** Cash/bank to hand back */
  refundCash: Decimal;
  issues: Array<'qty_exceeds' | 'qty_invalid' | 'unknown_line'>;
}

/**
 * Per-line netReversed = lineNet × (returned ÷ sold), round2; the final part gets the remainder so paisa match.
 * Money: bakiAdjust = min(total reversed, invoice's still-due amount) first; rest is cash refund (or exchange credit).
 */
export function computeReturn(lines: ReturnableLine[], req: ReturnRequestLine[], invoiceDue: MoneyInput): ReturnTotals {
  const issues: ReturnTotals['issues'] = [];
  const out: ReturnLineResult[] = [];
  for (const r of req) {
    const l = lines.find((x) => x.saleItemId === r.saleItemId);
    if (!l) {
      issues.push('unknown_line');
      continue;
    }
    const q = D(r.qty);
    const sold = D(l.qtySold);
    const left = sold.minus(l.returnedQty);
    if (q.lte(0)) {
      issues.push('qty_invalid');
      continue;
    }
    if (q.gt(left)) {
      issues.push('qty_exceeds');
      continue;
    }
    const final = q.eq(left);
    const netSoFar = D(l.netReversedSoFar ?? 0);
    const netReversed = final ? round2(D(l.lineNet).minus(netSoFar)) : round2(D(l.lineNet).times(q).dividedBy(sold));
    let costReversed: Decimal | null = null;
    if (r.restock && l.lineCost !== null && l.lineCost !== undefined) {
      const costSoFar = D(l.costReversedSoFar ?? 0);
      costReversed = final ? round2(D(l.lineCost).minus(costSoFar)) : round2(D(l.lineCost).times(q).dividedBy(sold));
    }
    out.push({ saleItemId: r.saleItemId, qty: q, restock: r.restock, netReversed, costReversed, final });
  }
  const totalReversed = sum(out.map((x) => x.netReversed));
  const bakiAdjust = Decimal.min(totalReversed, Decimal.max(D(invoiceDue), ZERO));
  return { lines: out, totalReversed, bakiAdjust, refundCash: totalReversed.minus(bakiAdjust), issues };
}
